import type { Observation } from '@medplum/fhirtypes'
import { codeableConcept } from '@seen/core/codeableConcept'
import {
  answersFitSideBySide,
  type CodingAnswerOption,
} from '@seen/core/codings'
import { featureFlags } from '@seen/core/featureFlags'
import { ExtendedObservation } from '@seen/core/models/observation'
import {
  type RecordedObservationAnswer,
  recordedObservationAnswers,
} from '@seen/core/observationReferences'
import { referenceStringToId } from '@seen/core/references'
import { generateUUID, now } from '@seen/core/utils'
import { cn } from '@seen/ui'
import {
  IconChevronDown,
  IconSquare,
  IconSquareCheckFilled,
} from '@tabler/icons-react'
import { type JSX, useEffect, useMemo, useRef, useState } from 'react'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { LastValueChip } from '#src/components/rich-text-editor/ObservationNode/LastValueChip.tsx'
import { ObservationAnswerSetNote } from '#src/components/rich-text-editor/ObservationNode/ObservationAnswerSetNote.tsx'
import {
  answerSegmentClass,
  answerValueClass,
} from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { useReportPopoverOpen } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import { observationDate } from '#src/components/rich-text-editor/ObservationNode/observationLastValue.ts'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { ObservationNoteSlot } from '#src/components/rich-text-editor/ObservationNode/ObservationNoteSlot.tsx'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import {
  CHOOSE_PARTICIPANT,
  captureRecordFor,
  isForeignRecord,
} from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import { SegmentedAnswers } from '#src/components/rich-text-editor/ObservationNode/SegmentedAnswers.tsx'
import type {
  ObservationCaptureViewProps,
  ObservationNodeAttributes,
  ObservationNodeOptions,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import {
  fromDraft,
  toDraft,
} from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useRemoveObservationRecord } from '#src/components/rich-text-editor/ObservationNode/useRemoveObservationRecord.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useGroupedObservationHistory } from '#src/hooks/useObservationHistory.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

const ANY_THAT_APPLY = 'Please select any that apply...'

type AnswerParams = Parameters<
  typeof ExtendedObservation.fromValueCodeableConcept
>[0]

// A choice that takes every answer that applies. Each selected answer is its
// own Observation; the node keeps them together. A short list stays in one
// segmented control. A longer list opens a checkbox menu, and once closed
// shows the chosen answers as one link.
export function ObservationNodeMultiChoice({
  capture,
  ...props
}: ObservationCaptureViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  const extensionOptions = props.extension.options as ObservationNodeOptions
  const { participant, noParticipant } = capture
  const practitioner = useCurrentPractitioner()
  const medplum = useMedplum()
  const createMutation = useCreateMutation<ExtendedObservation>()
  const removeRecord = useRemoveObservationRecord()
  const isReadOnly = useIsReadOnly()
  const editing = useFeatureFlag(featureFlags.structuredCapture)
  const answers = recordedObservationAnswers(attributes.references)
  const answersRef = useRef(answers)
  answersRef.current = answers
  // Deferred: the answers are draft Observations held on the node, one per
  // chosen answer, and nothing is written until the note is signed.
  const deferred = capture.deferred
  const heldDrafts = attributes.drafts ?? []
  const draftsRef = useRef<Observation[]>(heldDrafts)
  draftsRef.current = heldDrafts
  const draftObservations = useMemo(
    () => (attributes.drafts ?? []).map(fromDraft),
    [attributes.drafts]
  )
  /* Drafts stay on the node once the note is submitted, so a read of the
   * note shows its answers from the note itself, not from records it may
   * not have written yet. */
  const fromNote = deferred || heldDrafts.length > 0
  const answerCodes = fromNote
    ? draftObservations.map(draftCode)
    : answers.map((answer) => answer.code)
  // Assigned before the attribute round-trips, so two answers clicked together
  // share one key.
  const groupIdRef = useRef<string | null>(attributes.groupId)
  const previousGroupId = useRef(attributes.groupId)
  // The first answer's time. Later answers in the set reuse it.
  const recordedAtRef = useRef<string | null>(null)
  // A key minted for a click in progress is not on the node yet. Once the
  // node changes — including a clear back to unanswered — follow it.
  if (attributes.groupId !== previousGroupId.current) {
    groupIdRef.current = attributes.groupId
    if (!attributes.groupId) {
      recordedAtRef.current = null
    }
    previousGroupId.current = attributes.groupId
  }
  const [draftNote, setDraftNote] = useState('')
  const draftNoteRef = useRef(draftNote)
  draftNoteRef.current = draftNote
  const recordedIds = answers.map((answer) =>
    referenceStringToId(answer.reference)
  )
  const { data: recordedObservations } = useSearch(
    ExtendedObservation,
    recordedIds.length > 0 ? { _id: recordedIds.join(',') } : undefined,
    { silentErrors: true }
  )
  const recordedRef = useRef(recordedObservations)
  recordedRef.current = recordedObservations
  const hadAnswers = useRef(answerCodes.length > 0)
  useEffect(() => {
    if (answerCodes.length > 0) {
      hadAnswers.current = true
      return
    }
    if (hadAnswers.current) {
      hadAnswers.current = false
      setDraftNote('')
    }
  }, [answerCodes.length])
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set())
  const [open, setOpenState] = useState(false)
  const reportOpen = useReportPopoverOpen(false)

  const options = attributes.answerOptions ?? []
  const sideBySide = answersFitSideBySide(
    options.map((option) => option.display)
  )
  // Answers recorded for someone else than the editor's participant: shown,
  // and clearable, but not changed from here.
  const recordFor = captureRecordFor(capture)
  const foreign =
    !deferred &&
    (recordedObservations ?? []).some((observation) =>
      isForeignRecord(observation, recordFor)
    )
  const shownObservations = fromNote ? draftObservations : recordedObservations
  const locked =
    !editing || isReadOnly || noParticipant || !capture.editable || foreign
  const picked = (option: CodingAnswerOption): boolean =>
    answerCodes.includes(option.code)

  const setOpen = (next: boolean): void => {
    setOpenState(next)
    reportOpen(next)
  }

  const writeAnswers = (next: RecordedObservationAnswer[]): void => {
    answersRef.current = next
    const changes: Partial<ObservationNodeAttributes> = {
      references: next.length > 0 ? next : null,
    }
    if (groupIdRef.current) {
      changes.groupId = groupIdRef.current
    }
    props.updateAttributes(changes)
  }

  // One key for this node's answers, and the time of the first of them.
  const answerSet = async (): Promise<{
    groupId: string
    effectiveDateTime: string
  }> => {
    if (!groupIdRef.current) {
      groupIdRef.current = generateUUID()
    }
    if (!recordedAtRef.current && answersRef.current.length === 0) {
      recordedAtRef.current = now()
    }
    if (!recordedAtRef.current) {
      const first = answersRef.current[0]
      const existing = first
        ? await medplum
            .readResourceClass(
              ExtendedObservation,
              referenceStringToId(first.reference)
            )
            .catch(() => undefined)
        : undefined
      recordedAtRef.current = existing?.effectiveDateTime ?? now()
    }
    return {
      groupId: groupIdRef.current,
      effectiveDateTime: recordedAtRef.current,
    }
  }

  const writeDrafts = (next: Observation[]): void => {
    draftsRef.current = next
    const changes: Partial<ObservationNodeAttributes> = {
      drafts: next.length > 0 ? next : null,
    }
    if (groupIdRef.current) {
      changes.groupId = groupIdRef.current
    }
    props.updateAttributes(changes)
    extensionOptions.onReferencePersisted?.()
  }

  const toggleDraft = (option: CodingAnswerOption): void => {
    const current = draftsRef.current
    if (current.some((draft) => draftCode(fromDraft(draft)) === option.code)) {
      writeDrafts(
        current.filter((draft) => draftCode(fromDraft(draft)) !== option.code)
      )
      return
    }
    if (!groupIdRef.current) {
      groupIdRef.current = generateUUID()
    }
    const first = current[0]
    if (first?.effectiveDateTime) {
      recordedAtRef.current = first.effectiveDateTime
    } else if (!recordedAtRef.current) {
      recordedAtRef.current = now()
    }
    const note =
      current
        .map((draft) => fromDraft(draft).noteFirstText)
        .find((text) => text?.trim()) || draftNoteRef.current
    const params: AnswerParams = {
      valueCodeableConcept: codeableConcept(option),
      code: codeableConcept({
        code: attributes.code,
        system: attributes.system,
        display: attributes.codingDisplay,
      }),
      participant,
      practitioner: practitioner.reference,
      status: capture.recordStatus,
      answerSetId: groupIdRef.current,
      effectiveDateTime: recordedAtRef.current,
    }
    if (note.trim()) {
      params.note = note
    }
    const created = ExtendedObservation.fromValueCodeableConcept(params)
    writeDrafts([...current, toDraft(created)])
  }

  // The set's note, written onto every held answer.
  const writeDraftNote = (value: string): void => {
    writeDrafts(
      draftsRef.current.map((draft) => {
        const next = fromDraft(draft)
        next.setNoteFirst(value || undefined, practitioner.reference)
        next.performerPractitioner = practitioner.reference
        return toDraft(next)
      })
    )
  }

  const toggle = async (option: CodingAnswerOption): Promise<void> => {
    if (locked || pending.has(option.code)) {
      return
    }
    if (deferred) {
      toggleDraft(option)
      return
    }
    setPending((current) => new Set(current).add(option.code))
    try {
      const existing = answersRef.current.find(
        (answer) => answer.code === option.code
      )
      if (existing) {
        const observation = await medplum
          .readResourceClass(
            ExtendedObservation,
            referenceStringToId(existing.reference)
          )
          .catch(() => undefined)
        if (observation) {
          await removeRecord(observation)
        }
        writeAnswers(
          answersRef.current.filter((answer) => answer.code !== option.code)
        )
        return
      }
      const { groupId, effectiveDateTime } = await answerSet()
      const note =
        recordedRef.current
          ?.map((observation) => observation.noteFirstText)
          .find((text) => text?.trim()) || draftNoteRef.current
      const params: AnswerParams = {
        valueCodeableConcept: codeableConcept(option),
        code: codeableConcept({
          code: attributes.code,
          system: attributes.system,
          display: attributes.codingDisplay,
        }),
        participant,
        practitioner: practitioner.reference,
        status: capture.recordStatus,
        answerSetId: groupId,
        effectiveDateTime,
      }
      if (note.trim()) {
        params.note = note
      }
      const created = await createMutation.mutateAsync(
        ExtendedObservation.fromValueCodeableConcept(params)
      )
      writeAnswers([
        ...answersRef.current.filter((answer) => answer.code !== option.code),
        { reference: created.reference.reference, code: option.code },
      ])
      extensionOptions.onReferencePersisted?.()
    } finally {
      setPending((current) => {
        const next = new Set(current)
        next.delete(option.code)
        return next
      })
    }
  }

  const chosen = options.filter(picked)

  // The last multi-select answer from elsewhere, offered whole: one click
  // records every value of it that this field can take.
  const [lastCapture] = useGroupedObservationHistory({
    participant: editing ? participant : undefined,
    coding: { system: attributes.system, code: attributes.code },
    exceptGroupId: attributes.groupId,
    limit: 1,
  })
  const lastOptions = (lastCapture?.members ?? []).flatMap((member) => {
    const code = member.valueCodeableConcept?.coding?.[0]?.code
    const option = options.find((candidate) => candidate.code === code)
    return option ? [option] : []
  })
  const lastDate = lastCapture?.members[0]
    ? observationDate(lastCapture.members[0])
    : null
  const lastText = lastOptions.map((option) => option.display).join(', ')
  const repeatLast = async (): Promise<void> => {
    // One after another, so they share one answer set.
    for (const option of lastOptions) {
      if (!picked(option)) {
        await toggle(option)
      }
    }
  }

  return (
    <ObservationNodeFrame
      attributes={attributes}
      updateAttributes={capture.editable ? props.updateAttributes : undefined}
      blockedReason={noParticipant ? CHOOSE_PARTICIPANT : undefined}
      participant={participant}
      foreign={foreign}
    >
      {sideBySide ? (
        <SegmentedAnswers
          role="checkbox"
          label={attributes.codingDisplay}
          options={options}
          isPicked={picked}
          isDisabled={(option) => locked || pending.has(option.code)}
          onClick={(option) => void toggle(option)}
        />
      ) : (
        <DropdownAnswers
          open={open && !locked}
          onOpenChange={setOpen}
          options={options}
          chosen={chosen}
          picked={picked}
          locked={locked}
          pending={pending}
          onToggle={toggle}
        />
      )}
      {answerCodes.length === 0 && lastOptions.length > 0 && !isReadOnly && (
        <LastValueChip
          text={lastText}
          date={lastDate}
          ariaLabel={`Use last answers ${lastText}${lastDate ? ` from ${lastDate}` : ''}`}
          onClick={() => void repeatLast()}
          disabled={locked || noParticipant}
        />
      )}
      {shownObservations && shownObservations.length > 0 ? (
        <ObservationNoteSlot
          note={
            shownObservations
              .map((observation) => observation.noteFirstText)
              .find((text) => text?.trim()) ?? ''
          }
        >
          &nbsp;&nbsp;
          <ObservationAnswerSetNote
            observations={shownObservations}
            disabled={foreign}
            onDraftNote={deferred ? writeDraftNote : undefined}
            singleLine
            className="-ml-[0.1875rem] min-w-0 grow"
          />
        </ObservationNoteSlot>
      ) : answerCodes.length === 0 ? (
        <ObservationNoteSlot note={draftNote}>
          &nbsp;&nbsp;
          <ObservationRichTextEditor
            placeholder="Notes…"
            singleLine
            value={draftNote}
            onChange={setDraftNote}
            className="-ml-[0.1875rem] min-w-0 grow"
          />
        </ObservationNoteSlot>
      ) : null}
    </ObservationNodeFrame>
  )
}

function DropdownAnswers({
  open,
  onOpenChange,
  options,
  chosen,
  picked,
  locked,
  pending,
  onToggle,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  options: CodingAnswerOption[]
  chosen: CodingAnswerOption[]
  picked: (option: CodingAnswerOption) => boolean
  locked: boolean
  pending: ReadonlySet<string>
  onToggle: (option: CodingAnswerOption) => Promise<void>
}): JSX.Element {
  // While the menu is open the field keeps its prompt. Closed, chosen
  // answers read as one link.
  const prompt = open || chosen.length === 0
  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={false}>
      <PopoverTrigger asChild disabled={locked}>
        <button
          type="button"
          className={
            prompt
              ? cn(
                  answerSegmentClass(),
                  'text-foreground-tertiary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950 disabled:cursor-not-allowed disabled:opacity-60'
                )
              : answerValueClass()
          }
        >
          {prompt ? (
            <>
              {ANY_THAT_APPLY}
              <IconChevronDown
                className="size-4 shrink-0 text-foreground-tertiary"
                aria-hidden
              />
            </>
          ) : (
            chosen.map((option) => option.display).join(', ')
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto min-w-56 p-1"
        // Stay open so several answers can be checked in one pass.
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <span role="group" className="flex flex-col">
          {options.map((option) => {
            const selected = picked(option)
            return (
              <button
                key={`${option.system ?? ''}|${option.code}`}
                type="button"
                role="checkbox"
                aria-checked={selected}
                disabled={pending.has(option.code)}
                onClick={() => void onToggle(option)}
                className="flex items-center gap-2 rounded-xs px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-hidden disabled:opacity-60"
              >
                {selected ? (
                  <IconSquareCheckFilled
                    className="size-4 shrink-0 text-blue-600"
                    aria-hidden
                  />
                ) : (
                  <IconSquare
                    className="size-4 shrink-0 text-foreground-tertiary"
                    aria-hidden
                  />
                )}
                {option.display}
              </button>
            )
          })}
        </span>
      </PopoverContent>
    </Popover>
  )
}

function draftCode(observation: ExtendedObservation): string {
  return observation.valueCodeableConcept?.coding?.[0]?.code ?? ''
}
